import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, X } from "lucide-react";
import { Seo } from "@/components/layout/Seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { BeforeAfterSlider } from "@/components/home/BeforeAfter";
import { FinalCta } from "@/components/home/Sections";
import { CATEGORIES, REALISATIONS } from "@/data/realisations";
import { SITE } from "@/config/site";

const imageLd = REALISATIONS.map((r) => ({
  "@context": "https://schema.org",
  "@type": "ImageObject",
  contentUrl: r.after,
  name: r.title,
  description: r.description,
  creditText: "Visuel d'illustration (Unsplash)",
  acquireLicensePage: `${SITE.url}/realisations`,
}));

export default function Realisations() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const list = filter === "all" ? REALISATIONS : REALISATIONS.filter((r) => r.category === filter);

  return (
    <>
      <Seo
        title="Nos réalisations de rénovation en Île-de-France | O'RIZON 360"
        description="Galerie avant/après de nos travaux de maçonnerie, étanchéité, isolation, équipements techniques et rénovation d'immeubles en Île-de-France."
        jsonLd={imageLd}
      />
      <PageHero crumb="Réalisations" eyebrow="Réalisations" lines={["Avant, après :", "le travail parle"]} intro="Galerie filtrable par type de prestation. Les visuels présentés sont des illustrations libres de droits dans l'attente des photos de nos propres chantiers." />

      {/* EMPLACEMENT RÉSERVÉ — remplacer par de vraies photos de chantier avec l'accord du client (voir src/data/realisations.js). */}
      <section className="section bg-concrete">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-12" role="tablist" aria-label="Filtrer par prestation" data-testid="realisations-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                role="tab"
                aria-selected={filter === c.key}
                onClick={() => setFilter(c.key)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-[background-color,color,transform] duration-200 ${filter === c.key ? "bg-slate text-white" : "bg-white text-slate border border-line hover:border-copper hover:text-copper"}`}
                data-testid={`filter-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <motion.div layout className="grid md:grid-cols-2 xl:grid-cols-3 gap-8" data-testid="realisations-grid">
            <AnimatePresence mode="popLayout">
              {list.map((r) => (
                <motion.article
                  key={r.id}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.35 }}
                  className="card card-hover overflow-hidden group"
                  data-testid={`realisation-card-${r.id}`}
                >
                  <button type="button" onClick={() => setActive(r)} className="block w-full text-left" aria-label={`Voir le détail : ${r.title}`} data-testid={`realisation-open-${r.id}`}>
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={r.after} alt={`${r.title} — visuel d'illustration`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" width="1200" height="900" />
                      <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-widest bg-copper text-white px-3 py-1.5 rounded-full">{r.categoryLabel}</span>
                    </div>
                  </button>
                  <div className="p-6">
                    <h2 className="font-heading font-bold uppercase text-2xl text-slate">{r.title}</h2>
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-muted"><MapPin size={14} className="text-copper" /> {r.place}</p>
                    <p className="mt-3 text-sm text-ink/80 leading-relaxed">{r.description}</p>
                    <button type="button" onClick={() => setActive(r)} className="mt-4 text-sm font-semibold text-copper hover:underline underline-offset-2" data-testid={`realisation-detail-${r.id}`}>Comparer avant / après →</button>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
          {list.length === 0 && <p className="text-muted">Aucune réalisation dans cette catégorie pour le moment.</p>}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[80] bg-slate/80 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            role="dialog" aria-modal="true" aria-label={active.title}
            data-testid="realisation-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <p className="eyebrow mb-2">{active.categoryLabel} · {active.place}</p>
                  <h2 className="h-display text-3xl sm:text-4xl">{active.title}</h2>
                </div>
                <button onClick={() => setActive(null)} className="p-2 rounded-full hover:bg-concrete" aria-label="Fermer" data-testid="realisation-modal-close"><X /></button>
              </div>
              <BeforeAfterSlider before={active.before} after={active.after} alt={active.title} testId="modal-slider" />
              <p className="mt-6 text-ink/85 leading-relaxed">{active.description}</p>
              {active.gallery.length > 0 && (
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {active.gallery.map((g, i) => <img key={g} src={g} alt={`${active.title} — détail ${i + 1} (visuel d'illustration)`} className="rounded-xl aspect-[4/3] object-cover w-full" loading="lazy" width="800" height="600" />)}
                </div>
              )}
              <p className="mt-6 text-xs text-muted">Visuels d'illustration libres de droits — ne représentent pas des chantiers réalisés par O'RIZON 360.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Reveal className="bg-white border-t border-line">
        <div className="container-x py-10 text-sm text-muted">
          Les visuels de cette page sont des images d'illustration libres de droits (Unsplash). Ils seront remplacés par les photos de nos chantiers, avec l'accord de nos clients.
        </div>
      </Reveal>
      <FinalCta />
    </>
  );
}
